import { useEffect, useState } from "react";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";

import { ActivityControl } from "./activity-field";
import "./activity-modal.css";

export const MIN_DURATION = 1;
export const MAX_DURATION = 99;

const clamp = (value) =>
  Math.min(MAX_DURATION, Math.max(MIN_DURATION, value));

/**
 * LF360-1881 — how many days a task runs for.
 *
 * A dropdown of ninety-nine numbers before this, which is what the design
 * replaces: the number reads as a value rather than as a list to be searched.
 * The same ninety-nine are still reachable — the number is typed as well as
 * stepped, because walking from 1 to 60 a click at a time is not an improvement
 * on a dropdown, and the design's stepper alone would have been one.
 *
 * What is typed is held locally until it is finished with: committing on every
 * keystroke would send "3" on the way to "30", and each of those is a save.
 */
const DurationStepper = ({
  value = MIN_DURATION,
  onChange,
  disabled = false,
  unit = "Day",
  id,
  label = "Duration",
}) => {
  const current = clamp(Number(value) || MIN_DURATION);
  const [typed, setTyped] = useState(String(current));

  useEffect(() => {
    setTyped(String(current));
  }, [current]);

  const commit = (next) => {
    const wanted = clamp(next);

    if (wanted !== current) {
      onChange?.(wanted);
    }

    setTyped(String(wanted));
  };

  // An empty box is somebody midway through typing, not a request for zero.
  const commitTyped = () => commit(Number(typed) || current);

  return (
    <div className="activity-field__row">
      <ActivityControl className="activity-duration">
        <input
          id={id}
          className="activity-duration__value"
          type="text"
          inputMode="numeric"
          aria-label={label}
          value={typed}
          disabled={disabled}
          onChange={(event) =>
            setTyped(event.target.value.replace(/[^\d]/g, "").slice(0, 2))
          }
          onBlur={commitTyped}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitTyped();
            }
          }}
        />
        <span className="activity-duration__unit">{unit}</span>
      </ActivityControl>

      <div className="activity-stepper">
        <button
          type="button"
          className="activity-stepper__button"
          aria-label={`Decrease ${label.toLowerCase()}`}
          disabled={disabled || current <= MIN_DURATION}
          onClick={() => commit(current - 1)}
        >
          <MinusOutlined />
        </button>
        <span className="activity-stepper__divider" />
        <button
          type="button"
          className="activity-stepper__button"
          aria-label={`Increase ${label.toLowerCase()}`}
          disabled={disabled || current >= MAX_DURATION}
          onClick={() => commit(current + 1)}
        >
          <PlusOutlined />
        </button>
      </div>
    </div>
  );
};

export default DurationStepper;
